// Client-side report builder for the preview dashboard.
//
// buildHtmlReport(portfolio, config) -> a self-contained HTML string (inline CSS,
// no network, no scripts). It mirrors the real "Generate HTML Report" output but is
// built entirely in the browser from the demo fixture, so prospects can produce a
// real, downloadable report without the in-development backend.
//
// `config` is exactly what GenerateReportDialog emits:
//   { title, fund_names, sections:{overview,chart,cashflow_table,metrics:{tvpi,dpi,rvpi,irr}},
//     date_range:{type,from,to}, currency }

import { foldFund, currenciesOf } from "./demoPortfolio";

const BRAND = "#C41E2A";

// ─── formatting + escaping ──────────────────────────────────────────────────────

function fmt(amount, currency = "USD") {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const ratio = (v) => (v == null ? "—" : `${v.toFixed(2)}x`);

function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ─── cash-flow date filtering ─────────────────────────────────────────────────────

function inRange(dateISO, range) {
  if (!range || range.type === "all") return true;
  const now = new Date();
  const d = new Date(dateISO);
  if (range.type === "ytd") return d.getFullYear() === now.getFullYear();
  if (range.type === "last_quarter") {
    const q = new Date(now);
    q.setMonth(q.getMonth() - 3);
    return d >= q && d <= now;
  }
  if (range.type === "custom") {
    if (range.from && dateISO < range.from) return false;
    if (range.to && dateISO > range.to) return false;
    return true;
  }
  return true;
}

const TYPE_LABEL = {
  commitment: "Commitment",
  capital_call: "Capital call",
  distribution: "Distribution",
  nav_snapshot: "Statement",
};

// ─── sections ─────────────────────────────────────────────────────────────────────

function overviewTable(funds, currency) {
  const rows = funds.map((f) => {
    const x = foldFund(f);
    return `<tr>
      <td class="name">${esc(f.fundName)}<span class="portal">${esc(f.portal)}</span></td>
      <td class="num">${fmt(x.commitment, currency)}</td>
      <td class="num called">${fmt(x.called, currency)}</td>
      <td class="num dist">${fmt(x.distributed, currency)}</td>
      <td class="num nav">${x.nav == null ? '<span class="pending">Pending</span>' : fmt(x.nav, currency)}</td>
      <td class="num">${ratio(x.tvpi)}</td>
      <td class="num">${ratio(x.dpi)}</td>
    </tr>`;
  });

  const totals = funds.reduce(
    (a, f) => {
      const x = foldFund(f);
      a.commitment += x.commitment || 0;
      a.called += x.called || 0;
      a.distributed += x.distributed || 0;
      a.nav += x.nav || 0;
      if (x.nav == null) a.navPending = true;
      return a;
    },
    { commitment: 0, called: 0, distributed: 0, nav: 0, navPending: false }
  );
  const tTvpi = totals.called > 0 ? (totals.nav + totals.distributed) / totals.called : null;
  const tDpi = totals.called > 0 ? totals.distributed / totals.called : null;

  return `<table class="grid">
    <thead><tr>
      <th>Fund</th><th class="num">Commitment</th><th class="num">Called</th>
      <th class="num">Distributed</th><th class="num">NAV</th><th class="num">TVPI</th><th class="num">DPI</th>
    </tr></thead>
    <tbody>${rows.join("")}</tbody>
    <tfoot><tr>
      <td>Total${totals.navPending ? " <span class='pending'>*</span>" : ""}</td>
      <td class="num">${fmt(totals.commitment, currency)}</td>
      <td class="num called">${fmt(totals.called, currency)}</td>
      <td class="num dist">${fmt(totals.distributed, currency)}</td>
      <td class="num nav">${fmt(totals.nav, currency)}</td>
      <td class="num">${ratio(tTvpi)}</td>
      <td class="num">${ratio(tDpi)}</td>
    </tr></tfoot>
  </table>
  ${totals.navPending ? `<p class="foot-note">* NAV and TVPI exclude funds awaiting their first capital account statement.</p>` : ""}`;
}

function bars(funds, currency) {
  const folds = funds.map((f) => ({ f, x: foldFund(f) }));
  const max = Math.max(1, ...folds.map(({ x }) => Math.max(x.commitment || 0, x.called || 0, x.nav || 0, x.distributed || 0)));
  const bar = (label, val, color) => {
    if (!val) return "";
    const pct = Math.max(2, (val / max) * 100);
    return `<div class="bar-row"><span class="bar-label">${label}</span><span class="bar-track"><span class="bar-fill" style="width:${pct.toFixed(1)}%;background:${color}"></span></span><span class="bar-val">${fmt(val, currency)}</span></div>`;
  };
  return folds
    .map(({ f, x }) => `<div class="chart-card">
      <p class="chart-title">${esc(f.fundName)}</p>
      ${bar("Commitment", x.commitment, "#94A3B8")}
      ${bar("Called", x.called, "#DC2626")}
      ${bar("NAV", x.nav, "#059669")}
      ${bar("Distributed", x.distributed, "#2563EB")}
    </div>`)
    .join("");
}

function metricsBlock(funds, currency, metrics) {
  const cells = [];
  if (metrics.tvpi) cells.push(["TVPI", (x) => ratio(x.tvpi)]);
  if (metrics.dpi) cells.push(["DPI", (x) => ratio(x.dpi)]);
  if (metrics.rvpi) cells.push(["RVPI", (x) => ratio(x.nav != null && x.called > 0 ? x.nav / x.called : null)]);
  if (metrics.irr) cells.push(["IRR", () => '<span class="pending">In development</span>']);
  if (!cells.length) return "";

  const head = `<th>Fund</th>${cells.map(([l]) => `<th class="num">${l}</th>`).join("")}`;
  const body = funds
    .map((f) => {
      const x = foldFund(f);
      return `<tr><td class="name">${esc(f.fundName)}</td>${cells.map(([, fn]) => `<td class="num">${fn(x)}</td>`).join("")}</tr>`;
    })
    .join("");
  return `<table class="grid"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function cashflowTable(funds, range) {
  const rows = [];
  for (const f of funds) {
    for (const e of f.events) {
      if (e.rejected || e.synthetic) continue;
      if (e.type === "nav_snapshot") continue;
      if (!inRange(e.date, range)) continue;
      rows.push({ date: e.date, fund: f.fundName, currency: f.currency, type: e.type, docType: e.docType, amount: e.amount });
    }
  }
  rows.sort((a, b) => a.date.localeCompare(b.date));
  if (!rows.length) return `<p class="foot-note">No cash-flow activity in the selected date range.</p>`;
  const body = rows
    .map((r) => `<tr>
      <td>${esc(r.date)}</td>
      <td class="name">${esc(r.fund)}</td>
      <td>${esc(TYPE_LABEL[r.type] || r.type)}</td>
      <td class="num ${r.type === "distribution" ? "dist" : "called"}">${r.type === "distribution" ? "+" : "−"}${fmt(r.amount, r.currency)}</td>
    </tr>`)
    .join("");
  return `<table class="grid"><thead><tr><th>Date</th><th>Fund</th><th>Type</th><th class="num">Amount</th></tr></thead><tbody>${body}</tbody></table>`;
}

// ─── document ───────────────────────────────────────────────────────────────────

const STYLE = `
  *{box-sizing:border-box} body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#111827;margin:0;background:#f9fafb}
  .page{max-width:880px;margin:0 auto;padding:32px;background:#fff}
  header{border-bottom:3px solid ${BRAND};padding-bottom:16px;margin-bottom:8px}
  .wordmark{font-weight:800;letter-spacing:.18em;color:${BRAND};font-size:13px}
  h1{font-size:22px;margin:10px 0 2px}
  .sub{color:#6b7280;font-size:13px;margin:0}
  .badge{display:inline-block;margin-top:12px;padding:4px 10px;border-radius:999px;background:rgba(196,30,42,.08);color:${BRAND};font-size:11px;font-weight:600}
  .dev{margin:16px 0;padding:10px 14px;border:1px solid #FCD34D;background:#FFFBEB;border-radius:10px;color:#92400E;font-size:12px}
  h2{font-size:15px;margin:28px 0 4px;color:#111827}
  h3{font-size:12px;text-transform:uppercase;letter-spacing:.08em;color:#9ca3af;margin:18px 0 6px}
  table.grid{width:100%;border-collapse:collapse;font-size:13px;margin-top:6px}
  table.grid th,table.grid td{padding:7px 10px;border-bottom:1px solid #f0f0f0;text-align:left}
  table.grid th{font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#9ca3af;border-bottom:1px solid #e5e7eb}
  table.grid tfoot td{font-weight:700;border-top:2px solid #e5e7eb;border-bottom:none}
  .num{text-align:right;font-variant-numeric:tabular-nums}
  .called{color:#DC2626}.dist{color:#2563EB}.nav{color:#059669}
  .name{font-weight:600}.portal{display:block;font-weight:400;color:#9ca3af;font-size:11px}
  .pending{color:#b45309;font-style:italic}
  .chart-card{border:1px solid #f0f0f0;border-radius:10px;padding:12px 14px;margin:8px 0}
  .chart-title{font-weight:600;font-size:13px;margin:0 0 8px}
  .bar-row{display:flex;align-items:center;gap:8px;margin:4px 0;font-size:11px}
  .bar-label{width:84px;color:#6b7280}.bar-track{flex:1;background:#f3f4f6;border-radius:4px;height:10px;overflow:hidden}
  .bar-fill{display:block;height:100%}.bar-val{width:110px;text-align:right;font-variant-numeric:tabular-nums;color:#374151}
  .foot-note{color:#9ca3af;font-size:11px;margin-top:6px}
  footer{margin-top:32px;padding-top:14px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:11px}
`;

export function buildHtmlReport(portfolio, config = {}) {
  const sections = config.sections || {};
  const metrics = sections.metrics || {};
  const generatedAt = config.generatedAt || new Date().toISOString().slice(0, 10);

  let funds = portfolio.funds;
  if (Array.isArray(config.fund_names) && config.fund_names.length) {
    funds = funds.filter((f) => config.fund_names.includes(f.fundName));
  }

  const groups = currenciesOf(funds).map((cur) => {
    const groupFunds = funds.filter((f) => f.currency === cur);
    const parts = [`<h2>${esc(cur)} holdings</h2>`];
    if (sections.overview) parts.push(overviewTable(groupFunds, cur));
    if (sections.chart) {
      parts.push(`<h3>Portfolio by fund</h3>`);
      parts.push(bars(groupFunds, cur));
    }
    if (metrics.tvpi || metrics.dpi || metrics.rvpi || metrics.irr) {
      parts.push(`<h3>Performance metrics</h3>`);
      parts.push(metricsBlock(groupFunds, cur, metrics));
    }
    return parts.join("\n");
  });

  const cashflow = sections.cashflow_table
    ? `<h2>Cash-flow activity</h2>${cashflowTable(funds, config.date_range)}`
    : "";

  const title = esc(config.title || "Portfolio Report");

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${title}</title><style>${STYLE}</style></head>
<body><div class="page">
  <header>
    <div class="wordmark">ELEMENTAR</div>
    <h1>${title}</h1>
    <p class="sub">${esc(portfolio.investor)} · Generated ${esc(generatedAt)}</p>
    <span class="badge">Preview — generated from demo data</span>
  </header>
  <div class="dev">This is a preview built from sample documents to show how your reports will look. The automated pipeline behind it is still in development. Figures are shown in each fund's native currency.</div>
  ${groups.join("\n")}
  ${cashflow}
  <footer>ELEMENTAR · Preview report generated ${esc(generatedAt)} from demo data. Figures shown in each fund's native currency. For illustration only — not investment advice.</footer>
</div></body></html>`;
}
